// Central registry of per-route SEO metadata. Keep titles ≤60 chars,
// descriptions ≤160 chars. Brand suffix " | Banamind" is appended automatically.

export const SITE_URL = "https://banamind.ai";
export const SITE_NAME = "Banamind";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;

type LocalizedString = string | Partial<Record<"en" | "ar" | "hi" | "ru", string>>;

export type SeoEntry = {
  title: LocalizedString;
  description: LocalizedString;
  keywords?: string;
  noIndex?: boolean;
};

export const pickLocalized = (
  value: LocalizedString,
  lang: string,
): string => {
  if (typeof value === "string") return value;
  return value[lang as "en" | "ar" | "hi" | "ru"] ?? value.en ?? "";
};

export const seoConfig: Record<string, SeoEntry> = {
  "/": {
    title: {
      en: "WhatsApp AI for Construction Project Management",
      ar: "ذكاء اصطناعي على واتساب لإدارة مشاريع البناء",
      hi: "निर्माण परियोजना प्रबंधन के लिए WhatsApp AI",
      ru: "ПО для строительства и управления проектами с ИИ",
    },
    description: {
      en: "Capture every photo, voice note and document from your WhatsApp groups. Banamind organizes site updates, tracks progress and surfaces risks — automatically.",
      ar: "التقط كل صورة وملاحظة صوتية ومستند من مجموعات واتساب. ينظّم Banamind تحديثات الموقع ويتتبّع التقدّم ويكشف المخاطر — تلقائيًا.",
      hi: "अपने WhatsApp ग्रुप से हर फ़ोटो, वॉइस नोट और दस्तावेज़ कैप्चर करें। Banamind साइट अपडेट व्यवस्थित करता है, प्रगति ट्रैक करता है और जोखिमों को सामने लाता है — स्वचालित रूप से।",
      ru: "Banamind связывает полевые обновления, задачи, фотографии, документы, ход работ, риски и отчёты в едином контексте строительного проекта.",
    },
    keywords:
      "construction project management, WhatsApp AI, site progress, contractor software, AI inspection, document intelligence",
  },
  "/about": {
    title: {
      en: "About Banamind — Construction Runs on WhatsApp, We Turn It Into Intelligence",
      ar: "عن Banamind — البناء يدور على واتساب، ونحوّله إلى معلومات ذكية",
      hi: "Banamind के बारे में — निर्माण WhatsApp पर चलता है, हम इसे इंटेलिजेंस में बदलते हैं",
      ru: "О Banamind — стройка живёт в WhatsApp, мы превращаем её в аналитику",
    },
    description: {
      en: "Banamind is the WhatsApp-native AI layer for construction teams across the GCC and India. We capture every photo, voice note and document your crews already send — and turn it into a clean, searchable, decision-ready project record.",
      ar: "Banamind هي طبقة الذكاء الاصطناعي العاملة عبر واتساب لفِرق البناء في دول الخليج والهند. نلتقط كل صورة ورسالة صوتية ومستند ترسله طواقمك أصلاً — ونحوّلها إلى سجل مشروع نظيف وقابل للبحث وجاهز لاتخاذ القرار.",
      hi: "Banamind GCC और भारत की निर्माण टीमों के लिए WhatsApp में चलने वाली AI परत है। आपकी टीमें जो हर फ़ोटो, वॉइस नोट और दस्तावेज़ पहले से भेजती हैं, उन्हें हम कैप्चर करते हैं — और एक साफ़, खोजने योग्य, निर्णय के लिए तैयार प्रोजेक्ट रिकॉर्ड में बदल देते हैं।",
      ru: "Banamind — это слой ИИ для строительных команд в странах Персидского залива и Индии, работающий прямо в WhatsApp. Мы фиксируем каждое фото, голосовое сообщение и документ, которые присылают ваши бригады, и превращаем их в чистую, удобную для поиска картину проекта.",
    },
    keywords:
      "about banamind, construction ai company, whatsapp ai for construction, construction technology gcc, saudi arabia construction software, contech company india, banamind team, banamind mission",
  },
  "/affiliate": {
    title: {
      en: "Banamind Affiliate Program — 15% for 12 Months",
      ar: "أوصِ بـ Banamind — اربح 15% لمدة عام",
      hi: "Banamind सुझाएँ — 12 महीने तक 15% कमाएँ",
      ru: "Рекомендуйте Banamind — получайте 15% в течение года",
    },
    description: {
      en: "Know construction teams who need AI? Recommend Banamind and earn 15% commission on every referral — every month for a full year after they sign up.",
      ar: "تعرف فرق بناء بحاجة إلى الذكاء الاصطناعي؟ أوصِ بـ Banamind واربح عمولة 15% على كل إحالة — كل شهر لمدة عام كامل بعد اشتراكهم.",
      hi: "ऐसी कंस्ट्रक्शन टीमों को जानते हैं जिन्हें AI की ज़रूरत है? Banamind सुझाएँ और हर रेफरल पर 15% कमीशन कमाएँ — साइन अप के बाद एक साल तक हर महीने।",
      ru: "Знаете строительные команды, которым нужен ИИ? Рекомендуйте Banamind и получайте 15% комиссии с каждого приглашённого — каждый месяц в течение года после регистрации.",
    },
    keywords:
      "banamind affiliate program, construction software affiliate, referral commission, recurring affiliate income, refer construction teams",
  },
  "/pricing": {
    title: {
      en: "Banamind Construction Software — Pricing & Plans",
      ar: "Banamind لبرامج البناء — الأسعار والخطط",
      hi: "Banamind निर्माण सॉफ़्टवेयर — कीमतें और प्लान",
      ru: "Banamind — цены и тарифы",
    },
    description: {
      en: "Transparent pricing for the best construction management software. Free plan for small business teams, paid tiers scale with your projects. No setup fees.",
      ar: "أسعار شفافة لأفضل برامج إدارة البناء. خطة مجانية لفرق الأعمال الصغيرة، وباقات مدفوعة تتوسع مع مشاريعك. بدون رسوم إعداد.",
      hi: "सर्वश्रेष्ठ निर्माण प्रबंधन सॉफ्टवेयर के लिए पारदर्शी मूल्य निर्धारण। छोटे व्यवसायों के लिए मुफ्त प्लान, परियोजनाओं के साथ बढ़ने वाले सशुल्क टियर। कोई सेटअप शुल्क नहीं।",
      ru: "Прозрачные тарифы лучшего ПО для управления строительством. Бесплатный план для малого бизнеса, платные уровни масштабируются с вашими проектами. Без платы за подключение.",
    },
    keywords: "best construction management software,best construction project management software,construction management software,construction project management software,construction software for small business,construction management software for small business,free construction management software,cloud based construction management software,construction software pricing,construction app,top 10 construction management software,construction software programs,building construction software,construction management software cloud",
  },
  "/contact": {
    title: {
      en: "Contact Banamind",
      ar: "تواصل مع Banamind",
      hi: "Banamind से संपर्क करें",
      ru: "Связаться с Banamind",
    },
    description: {
      en: "Book a WhatsApp demo or email our team — we reply within 24 hours. Start your Banamind free trial in under 5 minutes. No credit card required.",
      ar: "احجز عرضاً توضيحياً عبر واتساب أو راسل فريق Banamind — نردّ خلال 24 ساعة. ابدأ تجربتك المجانية في أقل من 5 دقائق، دون الحاجة لبطاقة ائتمان.",
      hi: "WhatsApp पर डेमो बुक करें या Banamind टीम को ईमेल करें — 24 घंटे में जवाब। क्रेडिट कार्ड के बिना, 5 मिनट में निःशुल्क खाता शुरू करें।",
      ru: "Закажите демо через WhatsApp или напишите команде Banamind — ответим в течение 24 часов. Запуск бесплатного аккаунта занимает меньше 5 минут.",
    },
  },
  "/webinar": {
    // EN title: 41 chars → 41 + " | Banamind" (11) = 52 total ✅ (limit 60)
    // EN desc: 156 chars ✅ (limit 160)
    title: {
      en: "AI in Construction Webinar — 28 July 2026",
      ar: "ندوة الذكاء الاصطناعي في البناء — 28 يوليو 2026",
      hi: "कंस्ट्रक्शन AI वेबिनार — 28 जुलाई 2026",
      ru: "ИИ в строительстве — вебинар 28 июля 2026",
    },
    description: {
      en: "Free live webinar July 28, 2026: practical AI workflows already cutting delays, costs and rework on real GCC construction projects. 60 min, free recording.",
      ar: "ندوة مجانية مباشرة في 28 يوليو 2026: تدفقات عمل ذكاء اصطناعي عملية تقلّص التأخيرات والتكاليف في مشاريع البناء الحقيقية. 60 دقيقة، تسجيل مجاني.",
      hi: "मुफ्त लाइव वेबिनार 28 जुलाई 2026: व्यावहारिक AI वर्कफ़्लो जो GCC निर्माण परियोजनाओं में देरी और लागत घटा रहे हैं। 60 मिनट, मुफ्त रिकॉर्डिंग।",
      ru: "Бесплатный вебинар 28 июля 2026: практические ИИ-процессы, сокращающие задержки и затраты на реальных стройплощадках. 60 минут, запись бесплатно.",
    },
    keywords:
      "AI in construction webinar, construction AI Saudi Arabia, Vision 2030 construction, AI for contractors, construction technology webinar GCC",
  },
  "/integrations": {
    title: {
      en: "Integrations — WhatsApp, Telegram & Primavera P6",
      ru: "Интеграции Banamind с рабочими каналами и системами",
    },
    description: {
      en: "Connect Banamind with WhatsApp, Telegram, MAX, LINE, Primavera P6, ZATCA and more. One project record across every channel.",
      ru: "Подключайте доступные мессенджеры и корпоративные системы. Возможности и направление обмена зависят от выбранной конфигурации.",
    },
    keywords: "WhatsApp construction integration, Primavera P6, ZATCA, Telegram integration",
  },

  // Audience pages
  "/who-we-serve": {
    title: {
      en: "AI Software for Construction Companies of Any Size",
      ar: "برنامج بالذكاء الاصطناعي لشركات البناء بكل الأحجام",
      hi: "हर साइज़ की निर्माण कंपनियों के लिए AI सॉफ़्टवेयर",
      ru: "AI-софт для строительных компаний любого размера",
    },
    description: {
      en: "Whether you run one project or twenty, Banamind is construction company software that fits your team size and workflow. No complex setup. No enterprise overhead.",
      ar: "سواء كنت تدير مشروعاً واحداً أو عشرين، يناسب Banamind حجم فريقك وسير عملك. بدون إعداد معقد. بدون تكاليف المؤسسات الكبرى.",
      hi: "चाहे आप एक प्रोजेक्ट चलाएं या बीस, Banamind आपकी टीम के आकार और वर्कफ़्लो में फिट होता है। कोई जटिल सेटअप नहीं। कोई एंटरप्राइज़ ओवरहेड नहीं।",
      ru: "Ведёте ли вы один проект или двадцать, Banamind подстраивается под размер вашей команды и рабочий процесс. Никакой сложной настройки. Никаких корпоративных излишеств.",
    },
    keywords: "construction company software,contractor management software,general contractor software,software for general contractors,contractor management tools,contractor software,accounting software for contractors,subcontractor management software,project management software for general contractors,contractor software for small business,general contractor software for small business,best contractor management software,remodeling contractor software,commercial contractor software,building contractor software",
  },
  "/restoration": {
    title: "Restoration Software — Document Damage with Proof",
    description:
      "Photo-based restoration software. Time-stamped damage reports, before/after evidence, and customer-ready updates from one app.",
  },
  "/general-contractor": {
    title: "General Contractor Software for Site Documentation",
    description:
      "Run multiple projects from one feed. Capture progress via WhatsApp, generate reports, and prove every step of work.",
  },
  "/hvac": {
    title: "HVAC Software for Service & Install Documentation",
    description:
      "Capture HVAC installs and service jobs with photos, videos and voice notes. AI organizes everything by project and zone.",
  },
  "/landscaping": {
    title: "Landscaping Software — Show Your Crews' Best Work",
    description:
      "Document landscaping jobs from before to after. Build galleries, share updates, and turn projects into marketing.",
  },
  "/roofing": {
    title: "Roofing Software — Inspections, Proof & Reports",
    description:
      "Roof inspections, evidence-based job records, and AI defect detection from photos. Built for roofing pros.",
  },
  "/solar": {
    title: "Solar Installation Software — Document Every Install",
    description:
      "Track solar installs site-by-site with photo evidence, AI inspection and shareable progress updates.",
  },

  // Core feature pages
  "/construction-photo-documentation": {
    title: {
      en: "Automatic Construction Photo Documentation",
      ar: "التوثيق المصور التلقائي لمشاريع البناء",
      hi: "स्वचालित निर्माण फ़ोटो डॉक्यूमेंटेशन",
      ru: "Фотодокументация строительства с ИИ",
    },
    description: {
      en: "Construction photo documentation software that auto-captures every jobsite photo, video and voice note from WhatsApp. AI-tagged, searchable, zero extra steps.",
      ar: "برنامج توثيق صور البناء يلتقط تلقائياً كل صورة وفيديو وملاحظة صوتية من واتساب. مصنّف بالذكاء الاصطناعي وقابل للبحث. بدون خطوات إضافية.",
      hi: "निर्माण फोटो दस्तावेज़ीकरण सॉफ्टवेयर जो WhatsApp से हर जॉबसाइट फोटो, वीडियो और वॉइस नोट को स्वचालित रूप से कैप्चर करता है। AI-टैग, खोजने योग्य, शून्य अतिरिक्त चरण।",
      ru: "Собирайте фотографии, видео и голосовые сообщения из Telegram, MAX, WhatsApp и других каналов. ИИ-теги и поиск по материалам проекта.",
    },
    keywords: "construction photo documentation software, jobsite photo documentation, construction site photos, construction photo documentation, construction site photo documentation, site photo documentation software, construction site camera app",
  },
  "/construction-progress-tracking": {
    title: {
      en: "AI Construction Progress Tracking Software",
      ar: "تتبع تقدم المشاريع الإنشائية بالذكاء الاصطناعي",
      hi: "AI निर्माण प्रगति ट्रैकिंग सॉफ़्टवेयर",
      ru: "ИИ-контроль хода строительства",
    },
    description: {
      en: "Construction progress tracking software that turns WhatsApp photos into real % progress. Track construction progress by phase and zone with AI evidence checks.",
      ar: "برنامج تتبع تقدّم البناء يحوّل صور واتساب إلى نسبة تقدّم حقيقية. تتبّع التقدّم حسب المرحلة والمنطقة مع فحوصات الأدلة بالذكاء الاصطناعي.",
      hi: "निर्माण प्रगति ट्रैकिंग सॉफ्टवेयर जो WhatsApp फ़ोटो को वास्तविक % प्रगति में बदलता है। चरण और ज़ोन के अनुसार प्रगति ट्रैक करें।",
      ru: "Контролируйте ход строительства по этапам, зонам и задачам. Banamind связывает расчётный прогресс с фотографиями, документами и статусами работ.",
    },
    keywords: "construction progress tracking software,construction project tracking software,construction progress tracking,construction progress monitoring,construction progress monitoring software,construction tracking app,track construction progress,construction progress report,construction progress reporting,construction work progress report,weekly construction progress report,construction progress report software,how to track construction progress,construction project tracking software free,construction progress tracking spreadsheet",
  },
  "/construction-reports": {
    title: {
      en: "Automatic Construction Reporting Software",
      ar: "تقارير البناء — تلقائياً",
      hi: "निर्माण रिपोर्ट्स — ऑटोपायलट पर",
      ru: "ПО для автоматического создания строительных отчётов",
    },
    description: {
      en: "AI-generated construction reports and a live dashboard. Build daily, weekly and progress reports from real project data in minutes.",
      ar: "تقارير بناء بالذكاء الاصطناعي ولوحة معلومات حية. أنشئ تقارير يومية وأسبوعية ومرحلية من بيانات المشروع في دقائق.",
      hi: "AI-जनित निर्माण रिपोर्ट और लाइव डैशबोर्ड। प्रोजेक्ट डेटा से दैनिक, साप्ताहिक और प्रगति रिपोर्ट मिनटों में बनाएँ।",
      ru: "Готовьте ежедневные, еженедельные и поэтапные отчёты из задач, фотографий, документов и данных о ходе работ. Проверка и экспорт в PDF.",
    },
    keywords: "construction reporting software,construction reports,construction reporting,daily construction report,construction dashboard,construction reporting app,construction progress reporting,construction site reporting,construction weekly report,construction analytics,AI construction reports,construction site report app,types of construction reports,smart construction dashboard",
  },
  "/ai-construction-estimating": {
    title: {
      en: "AI Construction Estimating Software",
      ar: "برنامج تقدير تكاليف البناء بالذكاء الاصطناعي",
      hi: "AI कंस्ट्रक्शन एस्टिमेटिंग सॉफ्टवेयर",
      ru: "ПО для строительных смет с ИИ",
    },
    description: {
      en: "Create construction estimates from scope, photos and drawings. Review labor, materials, markup and projected profit before sharing a client-ready proposal.",
      ar: "أنشئ تقديرات البناء من نطاق العمل والصور والمخططات. راجع العمالة والمواد والزيادة والربح المتوقع قبل مشاركة العرض مع العميل.",
      hi: "स्कोप, फ़ोटो और ड्रॉइंग से कंस्ट्रक्शन एस्टिमेट बनाएं। क्लाइंट प्रपोज़ल शेयर करने से पहले लेबर, मटेरियल, मार्कअप और लाभ की समीक्षा करें।",
      ru: "Создавайте строительные сметы из описания работ, фотографий и чертежей. Проверяйте работы, материалы, наценку и прибыль до отправки заказчику.",
    },
    keywords: "ai construction estimator,ai construction estimating,ai construction estimate,construction estimating software,ai estimating software for contractors,construction cost estimating software,ai construction cost estimating,best ai construction estimating software,construction estimate generator,contractor estimating software",
  },
  "/construction-daily-log": {
    title: {
      en: "AI Construction Daily Log",
      ar: "السجل اليومي للأعمال — تلقائياً",
      hi: "डेली वर्क लॉग — ऑटोपायलट पर",
      ru: "Журнал работ в строительстве с ИИ",
    },
    description: {
      en: "A construction daily log app that turns WhatsApp updates, photos, voice notes and documents into a searchable construction daily log form and feed.",
      ar: "تطبيق سجل يومي للبناء يحوّل تحديثات واتساب والصور والملاحظات الصوتية والمستندات إلى نموذج سجل يومي قابل للبحث وتدفق منظم.",
      hi: "एक कंस्ट्रक्शन डेली लॉग ऐप जो व्हाट्सएप अपडेट्स, फोटो, वॉइस नोट्स और दस्तावेज़ों को खोजने योग्य कंस्ट्रक्शन डेली लॉग फॉर्म और फीड में बदलता है।",
      ru: "Собирайте фотографии, голосовые заметки, документы и статусы в хронологическую ленту проекта. Поиск, фильтры и ежедневные сводки.",
    },
    keywords: "construction daily log app,construction daily log form,construction daily logs,construction daily log,construction daily log software,construction daily log book,daily report construction,construction field report,construction site daily report software,daily field report construction",
  },
  "/construction-document-management": {
    title: {
      en: "AI Construction Document Management Software",
      ar: "وثائق تنفيذ المشاريع — تلقائياً",
      hi: "निर्माण दस्तावेज़ — ऑटोपायलट पर",
      ru: "Исполнительная документация в строительстве с ИИ",
    },
    description: {
      en: "Construction document management software that uses AI search, summaries and OCR to read every contract, RFI, submittal, permit and invoice across your projects.",
      ar: "برنامج إدارة مستندات البناء يستخدم البحث الذكي والملخصات وOCR لقراءة كل عقد وطلب معلومات وعرض وتصريح وفاتورة في مشاريعك.",
      hi: "कंस्ट्रक्शन डॉक्यूमेंट मैनेजमेंट सॉफ्टवेयर जो हर कॉन्ट्रैक्ट, RFI, सबमिटल, परमिट और इनवॉइस को पढ़ने के लिए एआई सर्च, सारांश और OCR का उपयोग करता है।",
      ru: "Распознавание текста, извлечение полей, ИИ-сводки и поиск по договорам, чертежам, исполнительной документации, разрешениям и счетам.",
    },
    keywords: "construction document management,construction document management software,construction document management system,construction document management systems,construction submittal software,construction contract management software,construction document control software,construction drawing management software,construction rfi software,construction document control",
  },
  "/ai-for-construction": {
    title: {
      en: "AI Assistant for Construction Project Management",
      ar: "مساعد ذكاء اصطناعي لإدارة مشاريع البناء",
      hi: "निर्माण परियोजना प्रबंधन के लिए AI असिस्टेंट",
      ru: "ИИ-ассистент для управления строительными проектами",
    },
    description: {
      en: "AI for construction project managers. Chat with your project data, generate documents, inspect photos and search every file with one construction AI assistant.",
      ar: "ذكاء اصطناعي لمديري مشاريع البناء. تحدّث مع بيانات مشروعك، أنشئ المستندات، افحص الصور وابحث في كل ملف عبر مساعد واحد.",
      hi: "निर्माण प्रोजेक्ट मैनेजरों के लिए AI सहायक। प्रोजेक्ट डेटा से चैट करें, दस्तावेज़ बनाएं, फ़ोटो जांचें और हर फ़ाइल खोजें।",
      ru: "ИИ-ассистент для руководителя строительного проекта: поиск по данным, черновики документов, анализ фотографий и ответы со ссылками на источники.",
    },
    keywords: "ai for construction,ai in construction,construction ai,ai construction tools,construction ai software,ai construction management,ai project management software,ai in construction management,ai in construction project management,best ai for construction,chatgpt for construction,construction ai tools,generative ai in construction,ai for construction industry,using ai in construction",
  },

  // Advanced feature pages
  "/construction-risk-management": {
    title: {
      en: "AI Construction Risk Management",
      ar: "إدارة مخاطر البناء بالذكاء الاصطناعي",
      hi: "AI निर्माण जोखिम प्रबंधन",
      ru: "ИИ-управление рисками строительных проектов",
    },
    description: {
      en: "Construction risk management software that surfaces schedule, cost and quality risks early. AI builds your risk register and suggests mitigations.",
      ar: "برنامج إدارة المخاطر في البناء يكشف مخاطر الجدول الزمني والتكلفة والجودة مبكراً. يبني الذكاء الاصطناعي سجل المخاطر من بيانات المشروع ويقترح خطط التخفيف قبل تفاقم المشكلات.",
      hi: "निर्माण जोखिम प्रबंधन सॉफ्टवेयर जो शेड्यूल, लागत और गुणवत्ता जोखिमों को जल्दी सामने लाता है। AI प्रोजेक्ट डेटा से आपका रिस्क रजिस्टर बनाता है और समस्याएँ बढ़ने से पहले शमन सुझाता है।",
      ru: "Ведите реестр рисков и выявляйте сигналы в задачах, графике, документах и подтверждениях. ИИ предлагает возможные меры реагирования.",
    },
    keywords: "risk management in construction,construction risk management,risk management in construction projects,risk management in construction industry,risk management in construction management,construction risk management software,construction risk assessment software,construction risk management plan,construction risk mitigation,risk management in construction project management,types of risk management in construction,construction risk register",
  },
  "/contractor-invoicing": {
    title: {
      en: "AI Contractor Invoicing with Proof of Work",
      ar: "الفواتير وإثبات الأعمال — تلقائياً",
      hi: "बिल और कार्य प्रमाण — ऑटोपायलट पर",
      ru: "Счета с подтверждением выполненных работ",
    },
    description: {
      en: "Contractor invoicing software for construction billing. Attach photo proof to every invoice, send payment links, and export to QuickBooks.",
      ar: "برنامج فوترة للمقاولين لإصدار فواتير البناء وطلبات الدفع. أرفق إثباتاً مصوراً بكل فاتورة، وأرسل روابط الدفع، وصدّر البيانات إلى QuickBooks وأنظمة محاسبة البناء.",
      hi: "निर्माण बिलिंग और पेमेंट एप्लिकेशन के लिए कॉन्ट्रैक्टर इनवॉइसिंग सॉफ्टवेयर। हर इनवॉइस के साथ फोटो प्रमाण जोड़ें, पेमेंट लिंक भेजें, और QuickBooks व कंस्ट्रक्शन अकाउंटिंग टूल्स में निर्यात करें।",
      ru: "Подготовьте счёт и приложите отчёт о ходе работ с фотографиями и документами. Banamind хранит проектные подтверждения и не заменяет бухгалтерскую систему.",
    },
    keywords: "contractor invoicing software,construction invoicing software,construction billing software,contractor invoice app,construction invoice software,construction billing,payment application construction,pay application construction,construction payment software,quickbooks for contractors,construction accounting software quickbooks,integrates with construction accounting,contractor billing software,electrical contractor invoicing software",
  },
  "/construction-scheduling": {
    title: {
      en: "AI Construction Scheduling Software",
      ar: "برنامج جدولة البناء بالذكاء الاصطناعي",
      hi: "AI निर्माण शेड्यूलिंग सॉफ़्टवेयर",
      ru: "Планирование строительства с ИИ",
    },
    description: {
      en: "Construction scheduling software that builds your timeline automatically. AI Gantt chart, CPM dependencies and planned vs actual tracking.",
      ar: "برنامج جدولة البناء يبني الجدول الزمني للمشروع تلقائياً. مخطط جانت بالذكاء الاصطناعي، تبعيات CPM، ومقارنة المخطط مقابل الفعلي من الموقع.",
      hi: "निर्माण शेड्यूलिंग सॉफ्टवेयर जो आपकी निर्माण टाइमलाइन को स्वचालित रूप से बनाता है। AI-जनित गैंट चार्ट, CPM निर्भरताएँ, फील्ड से नियोजित बनाम वास्तविक ट्रैकिंग।",
      ru: "Создавайте рабочий график, задавайте этапы, зависимости и ответственных, а затем сравнивайте план с подтверждённым ходом работ.",
    },
    keywords: "construction scheduling software,construction schedule software,construction scheduling programs,construction schedule management software,construction project scheduling,construction project schedule,construction timeline,construction planning software,construction schedule gantt chart,construction cpm schedule,construction scheduling software free,best construction scheduling software,construction schedule app,construction planning app,construction programme software,construction work schedule",
  },
  "/share-updates": {
    title: {
      en: "Construction Collaboration & Client Portal",
      ar: "برنامج تعاون البناء وبوابة تحديثات العملاء",
      hi: "कंस्ट्रक्शन कोलैबोरेशन सॉफ्टवेयर और क्लाइंट अपडेट पोर्टल",
      ru: "Совместная работа в строительстве и клиентский портал",
    },
    description: {
      en: "Construction collaboration software to share branded photo and video updates with clients, subs and stakeholders through a simple construction client portal.",
      ar: "برنامج تعاون للبناء لمشاركة تحديثات الصور والفيديو ذات العلامة التجارية مع العملاء والمقاولين وأصحاب المصلحة عبر بوابة عملاء بسيطة.",
      hi: "ब्रांडेड फोटो और वीडियो अपडेट क्लाइंट्स, सब्स और स्टेकहोल्डर्स के साथ एक सरल कंस्ट्रक्शन क्लाइंट पोर्टल के माध्यम से साझा करने वाला कंस्ट्रक्शन कोलैबोरेशन सॉफ्टवेयर।",
      ru: "Делитесь фотографиями, видео и отчётами с заказчиками, субподрядчиками и другими участниками через управляемые ссылки и роли доступа.",
    },
    keywords: "construction collaboration software,construction collaboration,construction communication app,construction client portal,construction stakeholders",
  },
  "/punch-list-software": {
    title: {
      en: "AI Construction Punch List Software",
      ar: "برنامج الإشراف وقائمة الملاحظات بالذكاء الاصطناعي",
      hi: "AI निर्माण पंच लिस्ट सॉफ़्टवेयर",
      ru: "Строительный контроль и замечания с ИИ",
    },
    description: {
      en: "AI punch list software and construction inspection app. Define evidence per task, detect defects from photos, and run quality control checklists from the field.",
      ar: "برنامج قوائم العيوب وتطبيق تفتيش البناء بالذكاء الاصطناعي. حدّد الأدلة لكل مهمة واكتشف العيوب من الصور وأدر قوائم ضبط الجودة من الموقع.",
      hi: "AI पंच लिस्ट सॉफ्टवेयर और निर्माण निरीक्षण ऐप। प्रति कार्य साक्ष्य परिभाषित करें, फ़ोटो से दोष पहचानें और गुणवत्ता जांच सूचियाँ चलाएँ।",
      ru: "Ведите замечания, задавайте требования к фотоподтверждениям и используйте ИИ для поиска возможных дефектов. Решение остаётся за специалистом.",
    },
    keywords: "punch list software,construction punch list software,punch list app,construction inspection software,construction quality control software,construction punch list,punch list application,construction quality management software,construction inspection app,construction site inspection software,construction punch list app,best punch list app,punch list management,construction quality control checklist,punch list app for android",
  },
  "/construction-compliance": {
    title: {
      en: "AI Construction Compliance Software",
      ar: "برنامج الامتثال في البناء بالذكاء الاصطناعي",
      hi: "AI निर्माण कम्प्लायंस सॉफ़्टवेयर",
      ru: "Контроль требований и документов в строительстве",
    },
    description: {
      en: "Construction compliance software that tracks regulations, ZATCA filings, safety docs and deadlines with AI alerts before approvals go overdue.",
      ar: "برنامج امتثال للبناء يتابع المتطلبات التنظيمية وإقرارات زاتكا ووثائق السلامة والمواعيد مع تنبيهات ذكية قبل تأخّر أي اعتماد.",
      hi: "कंप्लायंस सॉफ्टवेयर जो नियम, ZATCA फाइलिंग, सुरक्षा दस्तावेज़ और समय-सीमाएँ ट्रैक करता है, ओवरड्यू से पहले AI अलर्ट देता है।",
      ru: "Настраиваемые чек-листы, обязательные документы, ответственные и сроки. Banamind показывает недостающие позиции и помогает подготовить сводку к проверке.",
    },
    keywords: "construction compliance software, construction compliance, construction health and safety software, construction safety compliance software",
  },
  "/annotations": {
    title: "Photo Annotations — Mark Up Construction Photos",
    description:
      "Add arrows, text, shapes and highlights to job-site photos. Make problems impossible to ignore.",
  },
  "/tags-labels": {
    title: "Tags & Labels — Organize Site Photos Instantly",
    description:
      "Auto-tag photos and documents by stage, zone and work type. Find any item in two clicks.",
  },
  "/team-workflows": {
    title: "Team Workflows — Automate Tasks & Approvals",
    description:
      "Automate task assignments, approval chains and notifications. Your team stays aligned without micromanagement.",
  },
  "/project-dashboard": {
    title: "Construction Project Dashboard — Live KPIs",
    description:
      "One dashboard for progress, risks, budget and team performance. Updated live from the field.",
  },
  "/portfolio": {
    title: "Build a Portfolio of Completed Projects",
    description:
      "Turn finished projects into a beautiful portfolio for sales, marketing and bid wins.",
  },
  "/procurement-ai": {
    title: "Procurement AI for Construction — Smart Purchasing",
    description:
      "AI-driven procurement: compare vendors, track orders, and prevent budget overruns on materials.",
  },
  "/marketing-ai": {
    title: "Marketing AI — Turn Job Sites into Content",
    description:
      "Generate social posts, case studies and proposals from your project photos and updates with AI.",
  },

  // Trade-specific landing pages
  "/ai-for-general-contractors": {
    title: {
      en: "AI for General Contractors — Multi-Site",
      ar: "AI للمقاولين العامين — إدارة مشاريع متعددة",
      hi: "जनरल कॉन्ट्रैक्टर्स के लिए AI — मल्टी-साइट प्रबंधन",
      ru: "AI для генподрядчиков — управление несколькими объектами",
    },
    description: {
      en: "Run multiple projects from one feed. Banamind auto-captures WhatsApp photos, coordinates subs, and generates progress reports for general contractors — automatically.",
      ar: "أدِر مشاريع متعددة من لوحة واحدة. يلتقط Banamind صور واتساب تلقائياً، وينسّق المقاولين الفرعيين، ويُنشئ تقارير التقدم للمقاولين العامين.",
      hi: "एक फ़ीड से कई प्रोजेक्ट चलाएं। Banamind WhatsApp फ़ोटो कैप्चर करता है, सब्स को कोऑर्डिनेट करता है और जनरल कॉन्ट्रैक्टर्स के लिए प्रगति रिपोर्ट स्वचालित रूप से बनाता है।",
      ru: "Управляйте несколькими объектами из одной ленты. Banamind автоматически захватывает фото из WhatsApp, координирует субподрядчиков и формирует отчёты о ходе работ.",
    },
    keywords: "ai for general contractors, general contractor software, construction ai general contracting, general contractor project management, multi-project tracking, subcontractor coordination software",
  },
  "/ai-for-villa-construction": {
    title: {
      en: "AI for Villa Construction",
      ar: "AI لبناء الفلل",
      hi: "विला निर्माण के लिए AI",
      ru: "AI для строительства вилл",
    },
    description: {
      en: "Deliver premium villa builds with automated documentation. Client portals, change-order trails, milestone photos and handover reports — all via WhatsApp.",
      ar: "سلِّم مشاريع الفلل الفاخرة بتوثيق آلي متكامل. بوابات العملاء ومسارات أوامر التغيير وصور المراحل وتقارير التسليم — كل ذلك عبر واتساب.",
      hi: "स्वचालित डॉक्यूमेंटेशन के साथ प्रीमियम विला बिल्ड डिलीवर करें। क्लाइंट पोर्टल, चेंज-ऑर्डर ट्रेल, मील-स्टोन फ़ोटो और हैंडओवर रिपोर्ट — सब WhatsApp के ज़रिए।",
      ru: "Сдавайте элитные виллы с автоматической документацией. Клиентские порталы, история изменений, фото этапов и акты сдачи — всё через WhatsApp.",
    },
    keywords: "ai for villa construction, villa construction software, residential construction ai, villa project management, client portal construction, change order tracking software",
  },
  "/ai-for-renovation": {
    title: {
      en: "AI for Renovation — Photo Proof & Changes",
      ar: "AI للتجديد — إثبات قبل/بعد وأوامر التغيير",
      hi: "रेनोवेशन के लिए AI — पहले/बाद प्रमाण और चेंज ऑर्डर",
      ru: "AI для ремонта — фотодоказательства и изменения",
    },
    description: {
      en: "Stop renovation disputes before they start. Before/after photo trails, change-order documentation and client approval workflows — auto-organised from WhatsApp.",
      ar: "أوقف نزاعات التجديد قبل أن تبدأ. مسارات الصور قبل/بعد، وتوثيق أوامر التغيير، وسير موافقات العملاء — منظَّمة تلقائياً من واتساب.",
      hi: "रेनोवेशन विवाद शुरू होने से पहले रोकें। पहले/बाद फ़ोटो ट्रेल, चेंज-ऑर्डर डॉक्यूमेंटेशन और क्लाइंट अप्रूवल वर्कफ़्लो — WhatsApp से स्वचालित रूप से व्यवस्थित।",
      ru: "Предотвращайте споры по ремонту до их возникновения. Фотохроника до/после, документация по изменениям и рабочие процессы согласования — авто-организация из WhatsApp.",
    },
    keywords: "ai for renovation, renovation software, renovation project management, before after construction photos, change order documentation, renovation dispute proof",
  },
  "/ai-for-demolition": {
    title: {
      en: "AI for Demolition — Safety & Compliance",
      ar: "AI للهدم — الامتثال للسلامة وتوثيق الموقع",
      hi: "डेमोलिशन के लिए AI — सुरक्षा कम्प्लायंस और साइट डॉक्यूमेंटेशन",
      ru: "AI для сноса — безопасность и документация объекта",
    },
    description: {
      en: "Document every hazard survey, demolition phase and safety milestone. Auto-generated daily reports and compliance records from your WhatsApp groups.",
      ar: "وثِّق كل مسح للمخاطر ومرحلة هدم وإنجاز سلامة. تقارير يومية وسجلات امتثال مُنشأة تلقائياً من مجموعات واتساب.",
      hi: "हर खतरा सर्वे, डेमोलिशन चरण और सुरक्षा मील-स्टोन का दस्तावेज़ीकरण करें। WhatsApp ग्रुप से स्वचालित दैनिक रिपोर्ट और कम्प्लायंस रिकॉर्ड।",
      ru: "Документируйте каждое обследование рисков, этап сноса и веху по безопасности. Ежедневные отчёты и записи о соответствии нормам — авто-генерация из WhatsApp-групп.",
    },
    keywords: "ai for demolition, demolition software, demolition project management, pre-demolition survey, demolition compliance documentation, safety records construction",
  },
  "/ai-for-landscaping": {
    title: {
      en: "AI for Landscaping — Before/After Galleries",
      ar: "AI لتنسيق الحدائق — جداول زمنية وقبل/بعد",
      hi: "लैंडस्केपिंग के लिए AI — पहले/बाद टाइमलाइन और क्लाइंट गैलरी",
      ru: "AI для благоустройства — хроника до/после и портфолио",
    },
    description: {
      en: "Turn every landscaping project into a visual story. Before/after photo timelines, crew progress tracking and client galleries — captured automatically via WhatsApp.",
      ar: "حوِّل كل مشروع تنسيق حدائق إلى قصة مصورة. جداول زمنية للصور قبل/بعد، تتبع تقدم الفرق ومعارض العملاء — ملتقطة تلقائياً عبر واتساب.",
      hi: "हर लैंडस्केपिंग प्रोजेक्ट को एक विज़ुअल कहानी में बदलें। पहले/बाद फ़ोटो टाइमलाइन, क्रू प्रगति ट्रैकिंग और क्लाइंट गैलरी — WhatsApp के ज़रिए स्वचालित रूप से कैप्चर।",
      ru: "Превращайте каждый проект озеленения в визуальную историю. Фотохроника до/после, отслеживание бригад и клиентские галереи — автозахват через WhatsApp.",
    },
    keywords: "ai for landscaping, landscaping software, landscaping project management, before after landscaping photos, landscaping portfolio builder, crew documentation app",
  },
  "/ai-for-mep": {
    title: {
      en: "AI for MEP — As-Built Records & Compliance",
      ar: "AI لأعمال MEP — سجلات التنفيذ وما قبل الإخفاء",
      hi: "MEP के लिए AI — As-Built रिकॉर्ड और प्री-कंसीलमेंट डॉक्स",
      ru: "AI для ОВиК/ЭС/ВК — исполнительная документация",
    },
    description: {
      en: "Document every pipe, duct and cable before walls close. As-built records, compliance checklists and multi-discipline coordination — auto-organised from WhatsApp.",
      ar: "وثِّق كل أنبوب وقناة وكابل قبل إغلاق الجدران. سجلات التنفيذ وقوائم الامتثال والتنسيق متعدد التخصصات — منظَّمة تلقائياً من واتساب.",
      hi: "दीवारें बंद होने से पहले हर पाइप, डक्ट और केबल का दस्तावेज़ीकरण करें। As-built रिकॉर्ड, कम्प्लायंस चेकलिस्ट और मल्टी-डिसिप्लिन कोऑर्डिनेशन — WhatsApp से स्वचालित।",
      ru: "Документируйте каждую трубу, воздуховод и кабель до закрытия стен. Исполнительная документация, чек-листы соответствия и межотраслевая координация — авто-организация из WhatsApp.",
    },
    keywords: "ai for mep, mep software, mechanical electrical plumbing software, as-built documentation, mep compliance records, pre-concealment photos construction",
  },
  "/ai-for-solar": {
    title: {
      en: "AI for Solar Installation — Panel Photo Docs",
      ar: "AI لتركيب الطاقة الشمسية — توثيق الألواح والضمان",
      hi: "सोलर इंस्टॉलेशन के लिए AI — पैनल डॉक्स और वारंटी पैकेज",
      ru: "AI для солнечных установок — документация и гарантия",
    },
    description: {
      en: "Panel-level photo documentation, electrical inspection checklists and warranty packages — all generated automatically from your WhatsApp site groups.",
      ar: "توثيق مصوّر على مستوى كل لوح، وقوائم فحص كهربائي، وحزم الضمان — كلها مُنشأة تلقائياً من مجموعات واتساب.",
      hi: "पैनल-स्तर फ़ोटो डॉक्यूमेंटेशन, इलेक्ट्रिकल इंस्पेक्शन चेकलिस्ट और वारंटी पैकेज — सब WhatsApp साइट ग्रुप से स्वचालित रूप से जेनरेट।",
      ru: "Фотодокументация на уровне панели, чек-листы электроинспекции и гарантийные пакеты — всё генерируется автоматически из WhatsApp-групп объекта.",
    },
    keywords: "ai for solar installation, solar installation software, solar project documentation, panel photo documentation, solar warranty compliance, electrical inspection checklist",
  },
  "/ai-for-data-centers": {
    title: {
      en: "AI for Data Centers — Hyperscale Build Docs",
      ar: "AI لمراكز البيانات — توثيق بناء الحجم الهائل",
      hi: "डेटा सेंटर के लिए AI — हाइपरस्केल बिल्ड डॉक्यूमेंटेशन",
      ru: "AI для ЦОД — документация гиперскейл-строительства",
    },
    description: {
      en: "Mission-critical documentation for hyperscale builds. Zone-by-zone photo records, milestone verification dashboards and commissioning packages — auto-organised.",
      ar: "توثيق بالغ الأهمية لمشاريع البناء الهائلة. سجلات مصورة منطقة بمنطقة، لوحات التحقق من المراحل وحزم التشغيل — منظَّمة تلقائياً.",
      hi: "हाइपरस्केल बिल्ड के लिए मिशन-क्रिटिकल डॉक्यूमेंटेशन। ज़ोन-दर-ज़ोन फ़ोटो रिकॉर्ड, मील-स्टोन वेरिफिकेशन डैशबोर्ड और कमीशनिंग पैकेज — स्वचालित रूप से व्यवस्थित।",
      ru: "Критически важная документация для гиперскейл-проектов. Пофазные фотозаписи, дашборды верификации этапов и пакеты ввода в эксплуатацию — авто-организация.",
    },
    keywords: "ai for data centers, data center construction software, hyperscale construction documentation, commissioning documentation, milestone verification construction, data center project management",
  },
  "/ai-for-civil-engineering": {
    title: {
      en: "AI for Civil Engineering — Infrastructure",
      ar: "AI للهندسة المدنية — سجلات مشاريع البنية التحتية",
      hi: "सिविल इंजीनियरिंग के लिए AI — इंफ्रास्ट्रक्चर रिकॉर्ड",
      ru: "AI для гражданского строительства — записи инфраструктуры",
    },
    description: {
      en: "Keep multi-phase infrastructure projects documented from ground-breaking to handover. Government milestone reports, stakeholder dashboards and geo-tagged photo records.",
      ar: "وثِّق مشاريع البنية التحتية متعددة المراحل من وضع حجر الأساس حتى التسليم. تقارير الحكومة والمراحل ولوحات أصحاب المصلحة وسجلات الصور بالإحداثيات الجغرافية.",
      hi: "मल्टी-फेज़ इंफ्रास्ट्रक्चर प्रोजेक्ट को ग्राउंड-ब्रेकिंग से हैंडओवर तक दस्तावेज़ीकृत रखें। सरकारी मील-स्टोन रिपोर्ट, स्टेकहोल्डर डैशबोर्ड और जियो-टैग्ड फ़ोटो रिकॉर्ड।",
      ru: "Документируйте многоэтапные инфраструктурные проекты от закладки до сдачи. Правительственные отчёты по вехам, дашборды для стейкхолдеров и геотегированные фотозаписи.",
    },
    keywords: "ai for civil engineering, civil engineering software, infrastructure project management, government milestone reporting, civil construction documentation, multi-phase project tracking",
  },

  // Legal
  "/terms-of-service": {
    title: "Terms of Service",
    description: "Banamind terms of service. Read the rules and conditions for using our platform.",
    noIndex: true,
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description: "How Banamind collects, uses and protects your data. Read our full privacy policy.",
    noIndex: true,
  },
  "/cookie-policy": {
    title: "Cookie Policy",
    description: "How Banamind uses cookies and similar technologies on our website.",
    noIndex: true,
  },
  "/blog": {
    title: {
      en: "Banamind Blog — AI & Construction Technology Insights",
      ar: "مدونة Banamind — الذكاء الاصطناعي وتقنيات البناء",
      hi: "Banamind ब्लॉग — AI और निर्माण तकनीक की जानकारी",
      ru: "Блог Banamind — AI и технологии в строительстве",
    },
    description: {
      en: "Practical advice, expert analysis and product deep dives — written for working construction project managers.",
      ar: "نصائح عملية وتحليلات الخبراء ومراجعات معمّقة للمنتجات — مكتوبة لمديري مشاريع البناء العاملين على الأرض.",
      hi: "व्यावहारिक सलाह, विशेषज्ञ विश्लेषण और प्रोडक्ट डीप-डाइव — फ़ील्ड में काम करने वाले निर्माण PMs के लिए।",
      ru: "Практичные материалы, экспертный анализ и продуктовые разборы — для прорабов и руководителей строительства.",
    },
    keywords:
      "construction AI blog, AI in construction, construction technology, MENA construction, AI construction software, WhatsApp AI construction, Banamind blog",
  },
};

export const formatTitle = (title: string) =>
  title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

// Routes that ship a dedicated branded OG/social cover at /og/<slug>.png
// (generated by scripts/generate-page-og.mjs). Everything else falls back to
// the default site OG image. Keep this set in sync with the generator's PAGES
// list — the slug is the route with leading slash dropped and "/" → "-".
export const OG_IMAGE_ROUTES = new Set<string>([
  "/about",
  "/affiliate",
  "/who-we-serve",
  "/webinar",
  "/blog",
  "/pricing",
  "/contact",
  "/integrations",
  "/restoration",
  "/general-contractor",
  "/hvac",
  "/landscaping",
  "/roofing",
  "/solar",
  "/construction-photo-documentation",
  "/construction-progress-tracking",
  "/construction-reports",
  "/construction-daily-log",
  "/construction-document-management",
  "/ai-for-construction",
  "/construction-risk-management",
  "/contractor-invoicing",
  "/construction-scheduling",
  "/share-updates",
  "/punch-list-software",
  "/construction-compliance",
  "/annotations",
  "/tags-labels",
  "/team-workflows",
  "/project-dashboard",
  "/portfolio",
  "/procurement-ai",
  "/marketing-ai",
  // Trade-specific AI pages
  "/ai-for-general-contractors",
  "/ai-for-villa-construction",
  "/ai-for-renovation",
  "/ai-for-demolition",
  "/ai-for-landscaping",
  "/ai-for-mep",
  "/ai-for-solar",
  "/ai-for-data-centers",
  "/ai-for-civil-engineering",
]);

/** Resolve the OG/social image URL for a canonical route. */
export const ogImageForRoute = (route: string): string => {
  if (!OG_IMAGE_ROUTES.has(route)) return DEFAULT_OG_IMAGE;
  const slug = route.replace(/^\//, "").replace(/\//g, "-");
  return `${SITE_URL}/og/${slug}.png`;
};
